import { useMemo } from 'react'

export interface ListNavigationResult {
  onPrev?: () => void
  onNext?: () => void
  hasPrev: boolean
  hasNext: boolean
}

export interface UseListNavigationParams<T> {
  /** Ordered list the detail page navigates through — typically the cached table rows. */
  items: T[] | undefined
  /** Id of the item currently open in the detail page. Compared to `getId(item)`. */
  currentId: string | undefined
  /** Pulls the comparable id out of an item — e.g. `(row) => row.id`. */
  getId: (item: T) => string
  /** Called with the prev/next item when the user activates the corresponding arrow. */
  onNavigate: (item: T) => void
}

/**
 * Computes prev/next navigation handlers for a detail view that lives on
 * top of a list table. Returns `undefined` handlers when no neighbor
 * exists — callers should treat that as "disable the arrow".
 *
 * Generic over the list item type, so the same hook backs leads, quotes,
 * orders, customers, etc. The caller supplies how ids are read and how
 * navigation happens.
 */
export function useListNavigation<T>({
  items,
  currentId,
  getId,
  onNavigate,
}: UseListNavigationParams<T>): ListNavigationResult {
  return useMemo<ListNavigationResult>(() => {
    const list = items ?? []
    const index = currentId
      ? list.findIndex((item) => getId(item) === currentId)
      : -1
    if (index < 0) {
      return { hasPrev: false, hasNext: false }
    }
    const prev = index > 0 ? list[index - 1] : undefined
    const next = index < list.length - 1 ? list[index + 1] : undefined
    return {
      onPrev: prev ? () => onNavigate(prev) : undefined,
      onNext: next ? () => onNavigate(next) : undefined,
      hasPrev: prev != null,
      hasNext: next != null,
    }
  }, [items, currentId, getId, onNavigate])
}
